/**
 * Service Providers directory - Phase 8.
 * Filter by provider_type and specialty. Card grid.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  ExternalLink,
  Mail,
  Search,
  Wrench,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import { ServiceProvider } from '../types/capitalReadiness';

const ServiceProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [specialtyFilter, setSpecialtyFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await capitalReadinessService.fetchProviders({
      provider_type: typeFilter || undefined,
      specialty: specialtyFilter || undefined,
    });
    if (res.error) {
      toast.error(res.error);
    } else {
      setProviders(res.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, specialtyFilter]);

  const providerTypes = useMemo(() => {
    const set = new Set<string>();
    providers.forEach((p) => {
      if (p.provider_type) set.add(p.provider_type);
    });
    return Array.from(set).sort();
  }, [providers]);

  const specialties = useMemo(() => {
    const set = new Set<string>();
    providers.forEach((p) => {
      (p.specialties || []).forEach((s) => set.add(s));
    });
    return Array.from(set).sort();
  }, [providers]);

  const filtered = useMemo(() => {
    return providers.filter((p) => {
      if (search) {
        const q = search.toLowerCase();
        const hay =
          `${p.name} ${p.description || ''} ${(p.specialties || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [providers, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/capital-readiness"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to capital readiness
        </Link>

        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
            <Wrench className="w-6 h-6 mr-2 text-blue-600" />
            Service providers
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Directory of accountants, lawyers, pitch coaches, and advisors.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search providers..."
              className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All types</option>
            {providerTypes.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <select
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All specialties</option>
            {specialties.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading providers...
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500 text-sm">
            No providers found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="bg-white border border-gray-200 rounded-xl p-5 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-base font-semibold text-gray-900">
                    {p.name}
                  </h3>
                  {!p.active && (
                    <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-300">
                      Inactive
                    </span>
                  )}
                </div>
                {p.provider_type && (
                  <div className="text-xs text-blue-700 font-medium capitalize">
                    {p.provider_type.replace(/_/g, ' ')}
                  </div>
                )}
                {p.description && (
                  <p className="text-sm text-gray-600">{p.description}</p>
                )}
                {p.specialties && p.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {p.specialties.map((s, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[11px]"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 pt-2 text-xs">
                  {p.website_url && (
                    <a
                      href={p.website_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-blue-600 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Website
                    </a>
                  )}
                  {p.contact_email && (
                    <a
                      href={`mailto:${p.contact_email}`}
                      className="inline-flex items-center text-blue-600 hover:underline"
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      {p.contact_email}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ServiceProvidersPage;
